import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ToggleButton,
  ToggleButtonGroup,
  Card,
  CardContent,
  CardActions,
  Tabs,
  Tab,
  Collapse,
  Divider,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AttachFile,
  DeleteOutline,
  PictureAsPdf,
  Image as ImageIcon,
  PhotoCamera,
  ViewModule,
  TableRows,
  ExpandMore,
  ExpandLess,
  DirectionsCar,
  Speed,
  LocalGasStation,
  Settings,
  Close,
} from '@mui/icons-material';
import InputMask from 'react-input-mask';
import api from '../../services/api';
import { toast } from 'react-toastify';
import GaleriaFotos from '../../components/GaleriaFotos';
import { useAuth } from '../../context/AuthContext';
import {
  canAccessRelatorios,
  canCreateVeiculos,
  canDelete,
  canEditVeiculos,
  canViewLucros,
  canViewPrecoCompra,
  ROLES,
} from '../../utils/permissions';

export default function Veiculos() {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [veiculos, setVeiculos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState('');
  const [abaAtiva, setAbaAtiva] = useState(0);
  const [fotosGaleria, setFotosGaleria] = useState([]);
  const [expandedVeiculo, setExpandedVeiculo] = useState(null);
  const [veiculoDetalhes, setVeiculoDetalhes] = useState(null);
  const detalhesRef = useRef(null);
  const [formData, setFormData] = useState({
    marca: '',
    modelo: '',
    placa: '',
    cor: '',
    ano_fabricacao: '',
    ano_modelo: '',
    renavam: '',
    chassi: '',
    quilometragem: '',
    combustivel: 'Flex',
    cambio: 'Manual',
    preco_compra: '',
    preco_venda: '',
    origem_estoque: 'proprio',
    fipe_mes_referencia: '',
    fipe_valor: '',
    observacoes: '',
  });
  const [documentos, setDocumentos] = useState([]);
  const [documentosSalvos, setDocumentosSalvos] = useState([]);
  const [lucroCalculado, setLucroCalculado] = useState({ lucro: 0, percentual: 0 });
  const podeEditarVeiculos = canEditVeiculos(usuario);
  const podeCadastrarVeiculos = canCreateVeiculos(usuario);
  const podeExcluirVeiculos = canDelete(usuario);
  const podeVerLucros = canViewLucros(usuario);
  const podeVerPrecoCompra = canViewPrecoCompra(usuario);
  const podeEmitirRelatorio = canAccessRelatorios(usuario);
  const isAdmin = usuario?.nivel_acesso === ROLES.ADMIN;

  useEffect(() => {
    loadVeiculos();
  }, []);

  // Verificar se veio do Dashboard com um Veículo específico
  useEffect(() => {
    if (location.state?.veiculoId && veiculos.length > 0) {
      const veiculo = veiculos.find(v => v.id === location.state.veiculoId);
      if (veiculo) {
        handleExpandVeiculo(veiculo);
      }
      // Limpar o state para não reabrir ao navegar
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, veiculos]);

  useEffect(() => {
    calcularLucro();
  }, [formData.preco_compra, formData.preco_venda]);

  useEffect(() => {
    if (expandedVeiculo && detalhesRef.current) {
      detalhesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandedVeiculo]);

  const loadVeiculos = async () => {
    try {
      const response = await api.get('/veiculos', { params: { limit: 1000 } });
      const data = response.data.veiculos || response.data || [];
      setVeiculos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar Veículos:', error);
      toast.error('Erro ao carregar Veículos');
      setVeiculos([]);
    }
  };

  const calcularLucro = () => {
    const compra = parseFloat(formData.preco_compra) || 0;
    const venda = parseFloat(formData.preco_venda) || 0;

    if (compra > 0 && venda > 0) {
      const lucro = venda - compra;
      const percentual = ((lucro / compra) * 100).toFixed(2);
      setLucroCalculado({ lucro, percentual });
    } else {
      setLucroCalculado({ lucro: 0, percentual: 0 });
    }
  };

  const loadFotosGaleria = async (veiculoId) => {
    try {
      const response = await api.get(`/veiculos/${veiculoId}/galeria`);
      setFotosGaleria(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar fotos da galeria:', error);
      setFotosGaleria([]);
    }
  };

  // Expandir detalhes do Veículo
  const handleExpandVeiculo = async (veiculo) => {
    if (expandedVeiculo === veiculo.id) {
      setExpandedVeiculo(null);
      setVeiculoDetalhes(null);
    } else {
      setExpandedVeiculo(veiculo.id);
      setVeiculoDetalhes(veiculo);
      if (veiculo.id) {
        try {
          const [detalhesResponse, galeriaResponse] = await Promise.all([
            api.get(`/veiculos/${veiculo.id}`),
            api.get(`/veiculos/${veiculo.id}/galeria`),
          ]);
          setVeiculoDetalhes({
            ...detalhesResponse.data,
            fotosGaleria: galeriaResponse.data || [],
            arquivos: detalhesResponse.data?.arquivos || [],
          });
        } catch (error) {
          console.error('Erro ao carregar detalhes do veículo:', error);
          setVeiculoDetalhes({ ...veiculo, fotosGaleria: [] });
        }
      }
    }
  };

  const loadArquivosVeiculo = async (veiculoId) => {
    try {
      const response = await api.get(`/veiculos/${veiculoId}/arquivos`);
      setDocumentosSalvos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar arquivos do veículo:', error);
      setDocumentosSalvos([]);
    }
  };

  const openArquivo = (arquivo) => {
    if (!arquivo?.url) return;
    const fullUrl = arquivo.url.startsWith('http') ? arquivo.url : `${window.location.origin}${arquivo.url}`;
    window.open(fullUrl, '_blank', 'noopener,noreferrer');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
    return `${(bytes / 1024).toFixed(2)} KB`;
  };

  const handleDeleteArquivoSalvo = async (arquivoId) => {
    if (!editId) return;
    try {
      await api.delete(`/veiculos/${editId}/arquivos/${arquivoId}`);
      setDocumentosSalvos((prev) => prev.filter((arquivo) => arquivo.id !== arquivoId));
      toast.success('Documento removido com sucesso');
    } catch (error) {
      console.error('Erro ao remover documento do veículo:', error);
      toast.error(error.response?.data?.error || 'Erro ao remover documento');
    }
  };

  const handleOpenDialog = async (veiculo = null) => {
    if (!podeEditarVeiculos) {
      toast.error('Você não tem permissão para editar veículos');
      return;
    }
    if (veiculo) {
      const { foto_url, foto_path, fotos, arquivos, fotosGaleria, venda, reservas, manutencoes, vendedor_original, ...rest } = veiculo;
      setEditId(veiculo.id);
      setFormData({
        marca: '',
        modelo: '',
        placa: '',
        cor: '',
        ano_fabricacao: '',
        ano_modelo: '',
        renavam: '',
        chassi: '',
        quilometragem: '',
        combustivel: 'Flex',
        cambio: 'Manual',
        preco_compra: '',
        preco_venda: '',
        origem_estoque: 'proprio',
        fipe_mes_referencia: '',
        fipe_valor: '',
        observacoes: '',
        ...rest,
      });
      setFotoPreview(foto_url || '');
      setFotoFile(null);
      setDocumentos([]);
      setDocumentosSalvos([]);
      setFotosGaleria(fotos || []);
      setAbaAtiva(0);

      if (veiculo.id) {
        await loadFotosGaleria(veiculo.id);
        await loadArquivosVeiculo(veiculo.id);
      }
    } else {
      setEditId(null);
      setFormData({
        marca: '',
        modelo: '',
        placa: '',
        cor: '',
        ano_fabricacao: '',
        ano_modelo: '',
        renavam: '',
        chassi: '',
        quilometragem: '',
        combustivel: 'Flex',
        cambio: 'Manual',
        preco_compra: '',
        preco_venda: '',
        origem_estoque: 'proprio',
        fipe_mes_referencia: '',
        fipe_valor: '',
        observacoes: '',
      });
      setFotoPreview('');
      setFotoFile(null);
      setDocumentos([]);
      setDocumentosSalvos([]);
      setFotosGaleria([]);
      setAbaAtiva(0);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditId(null);
    setDocumentos([]);
    setDocumentosSalvos([]);
    setFotoPreview('');
    setFotoFile(null);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => {
      const isValidType = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(
        file.type
      );
      if (!isValidType) {
        toast.error(`Arquivo ${file.name} não é PDF, PNG ou JPG`);
      }
      return isValidType;
    });

    setDocumentos([...documentos, ...validFiles]);
    toast.success(`${validFiles.length} arquivo(s) adicionado(s)`);
  };

  const handleRemoveFile = (index) => {
    const newDocumentos = documentos.filter((_, i) => i !== index);
    setDocumentos(newDocumentos);
    toast.info('Arquivo removido');
  };

  const handleFotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }
    setFotoFile(file);
    setFotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    try {
      if (
        !formData.marca ||
        !formData.modelo ||
        !formData.placa ||
        !formData.ano_modelo ||
        !formData.preco_compra ||
        !formData.preco_venda
      ) {
        toast.error(
          'Preencha os campos obrigatórios: Marca, Modelo, Placa, Ano, Preço de Compra e Preço de Venda'
        );
        return;
      }

      const payload = {
        ...formData,
        preco_compra: parseFloat(formData.preco_compra),
        preco_venda: parseFloat(formData.preco_venda),
        quilometragem: formData.quilometragem !== '' ? parseInt(formData.quilometragem, 10) : null,
        fipe_valor: formData.fipe_valor !== '' ? parseFloat(formData.fipe_valor) : null,
        fipe_mes_referencia: formData.fipe_mes_referencia || null,
      };

      let veiculoId = editId;
      if (editId) {
        const response = await api.put(`/veiculos/${editId}`, payload);
        veiculoId = response.data.id || editId;
        toast.success('Veículo atualizado com sucesso');
      } else {
        const response = await api.post('/veiculos', payload);
        veiculoId = response.data.id;
        toast.success('Veículo cadastrado com sucesso');
      }

      if (fotoFile && veiculoId) {
        const formDataFoto = new FormData();
        formDataFoto.append('file', fotoFile);
        await api.post(`/veiculos/${veiculoId}/foto`, formDataFoto, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (documentos.length > 0) {
        let enviados = 0;
        for (const documento of documentos) {
          const formDataArquivo = new FormData();
          formDataArquivo.append('file', documento);
          await api.post(`/veiculos/${veiculoId}/arquivos`, formDataArquivo, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          enviados += 1;
        }
        toast.success(`${enviados} documento(s) salvo(s) no veículo`);
      }

      handleCloseDialog();
      loadVeiculos();
    } catch (error) {
      toast.error(error.response.data.error || 'Erro ao salvar Veículo');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Excluir este veículo permanentemente?\nManutenções, avaliações, reservas e vendas vinculadas também serão excluídas.')) {
      try {
        await api.delete(`/veiculos/${id}`);
        toast.success('Veículo excluído com sucesso');
        loadVeiculos();
        if (expandedVeiculo === id) {
          setExpandedVeiculo(null);
          setVeiculoDetalhes(null);
        }
      } catch (error) {
        console.error('Erro ao excluir veículo:', error);
        toast.error(error.response?.data?.error || 'Erro ao excluir veículo');
      }
    }
  };

  const handleDownloadRelatorioVeiculo = async (veiculoId) => {
    try {
      const response = await api.get(`/veiculos/${veiculoId}/relatorio`);
      if (response.data?.url) {
        const fullUrl = `${window.location.origin}${response.data.url}`;
        window.open(fullUrl, '_blank');
      }
    } catch (error) {
      console.error('Erro ao gerar relatório do veículo:', error);
      toast.error('Erro ao gerar relatório do veículo');
    }
  };

  const handleEstornarVenda = async () => {
    if (!veiculoDetalhes?.venda?.id) {
      toast.error('Venda vinculada não encontrada para este veículo');
      return;
    }

    if (!window.confirm('Excluir a venda vinculada e devolver o veículo ao estoque como disponível?')) {
      return;
    }

    try {
      await api.delete(`/vendas/${veiculoDetalhes.venda.id}`);
      toast.success('Venda estornada. Veículo devolvido ao estoque.');
      await loadVeiculos();
      setExpandedVeiculo(null);
      setVeiculoDetalhes(null);
    } catch (error) {
      console.error('Erro ao estornar venda:', error);
      toast.error(error.response?.data?.error || 'Erro ao estornar venda');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      disponivel: 'success',
      reservado: 'warning',
      vendido: 'error',
      manutencao: 'info',
      inativo: 'default',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      disponivel: 'Disponível',
      reservado: 'Reservado',
      vendido: 'Vendido',
      manutencao: 'Em Manutenção',
      inativo: 'Inativo',
    };
    return labels[status] || status;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);
  };

  const getFileIcon = (file) => {
    if ((file.type || file.mime_type) === 'application/pdf') {
      return <PictureAsPdf color="error" />;
    }
    return <ImageIcon color="primary" />;
  };

  // Card de detalhes expandido
  const renderDetalhesExpandidos = () => {
    if (!veiculoDetalhes) return null;

    const lucro = parseFloat(veiculoDetalhes.preco_venda) - parseFloat(veiculoDetalhes.preco_compra);
    const lucroPerc = parseFloat(veiculoDetalhes.preco_compra) > 0
      ? ((lucro / parseFloat(veiculoDetalhes.preco_compra)) * 100).toFixed(2)
      : 0;

    const fotoPrincipal = veiculoDetalhes.fotos?.find(f => f.is_principal)?.url || veiculoDetalhes.foto_url;
    const todasFotos = veiculoDetalhes.fotosGaleria || veiculoDetalhes.fotos || [];

    return (
      <Paper
        ref={detalhesRef}
        sx={{
          p: 3,
          mb: 3,
          border: '2px solid',
          borderColor: 'primary.main',
          background: 'linear-gradient(135deg, rgba(214,40,40,0.05) 0%, rgba(0,0,0,0.4) 100%)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {veiculoDetalhes.marca} {veiculoDetalhes.modelo}
            </Typography>
            <Typography variant="h6" color="textSecondary">
              {veiculoDetalhes.placa} • {veiculoDetalhes.ano_fabricacao}/{veiculoDetalhes.ano_modelo}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Chip
              label={getStatusLabel(veiculoDetalhes.status)}
              color={getStatusColor(veiculoDetalhes.status)}
              size="medium"
            />
            {isAdmin && veiculoDetalhes.status === 'vendido' && veiculoDetalhes.venda?.id && (
              <Button variant="outlined" color="warning" onClick={handleEstornarVenda}>
                Estornar Venda
              </Button>
            )}
            <IconButton onClick={() => { setExpandedVeiculo(null); setVeiculoDetalhes(null); }}>
              <Close />
            </IconButton>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* Galeria de Fotos */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Fotos do Veículo
              </Typography>

              {/* Foto Principal */}
              <Box
                sx={{
                  height: 250,
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  mb: 2,
                }}
              >
                {fotoPrincipal ? (
                  <Box
                    component="img"
                    src={fotoPrincipal}
                    alt={`${veiculoDetalhes.marca} ${veiculoDetalhes.modelo}`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Box textAlign="center">
                    <DirectionsCar sx={{ fontSize: 64, color: 'text.disabled' }} />
                    <Typography color="textSecondary">Sem foto</Typography>
                  </Box>
                )}
              </Box>

              {/* Miniaturas */}
              {todasFotos.length > 0 && (
                <ImageList cols={4} rowHeight={80} gap={8}>
                  {todasFotos.slice(0, 8).map((foto, index) => (
                    <ImageListItem key={index}>
                      <Box
                        component="img"
                        src={foto.url}
                        alt={`Foto ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.8 },
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
              {todasFotos.length > 8 && (
                <Typography variant="caption" color="textSecondary">
                  +{todasFotos.length - 8} fotos adicionais
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Informações do Veículo */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Informações do Veículo
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <DirectionsCar fontSize="small" color="primary" />
                    <Typography variant="body2" color="textSecondary">Cor</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.cor || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Speed fontSize="small" color="primary" />
                    <Typography variant="body2" color="textSecondary">Quilometragem</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.quilometragem
                      ? `${parseInt(veiculoDetalhes.quilometragem).toLocaleString('pt-BR')} km`
                      : '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocalGasStation fontSize="small" color="primary" />
                    <Typography variant="body2" color="textSecondary">Combustível</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.combustivel || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Settings fontSize="small" color="primary" />
                    <Typography variant="body2" color="textSecondary">Câmbio</Typography>
                  </Box>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.cambio || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">RENAVAM</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.renavam || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Chassi</Typography>
                  <Typography variant="body1" fontWeight="medium" sx={{ wordBreak: 'break-all' }}>
                    {veiculoDetalhes.chassi || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Origem do Veículo</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.origem_estoque === 'consignado' ? 'Consignado' : 'Próprio'}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">FIPE</Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {veiculoDetalhes.fipe_valor
                      ? `${veiculoDetalhes.fipe_mes_referencia || 'Sem mês'} • ${formatCurrency(veiculoDetalhes.fipe_valor)}`
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Valores */}
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                background: 'linear-gradient(135deg, rgba(58,167,109,0.1) 0%, rgba(0,0,0,0.3) 100%)',
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Valores
              </Typography>
              {podeVerLucros ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Preço de Compra</Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(veiculoDetalhes.preco_compra)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Preço de Venda</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(veiculoDetalhes.preco_venda)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Lucro Estimado</Typography>
                    <Typography variant="h5" fontWeight="bold" color={lucro >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(lucro)} ({lucroPerc}%)
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">Preço de Venda</Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {formatCurrency(veiculoDetalhes.preco_venda)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>

          {/* Observações */}
          {veiculoDetalhes.observacoes && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Observações
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {veiculoDetalhes.observacoes}
                </Typography>
              </Paper>
            </Grid>
          )}

          {Array.isArray(veiculoDetalhes.arquivos) && veiculoDetalhes.arquivos.length > 0 && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Documentos Anexados
                </Typography>
                <List dense>
                  {veiculoDetalhes.arquivos.map((arquivo) => (
                    <ListItem
                      key={arquivo.id}
                      onClick={() => openArquivo(arquivo)}
                      sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      {getFileIcon(arquivo)}
                      <ListItemText
                        primary={arquivo.nome_original}
                        secondary={formatFileSize(arquivo.tamanho)}
                        sx={{ ml: 2 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}

          {/* Ações */}
          <Grid item xs={12}>
            <Box display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
              {podeEmitirRelatorio && (
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdf />}
                  onClick={() => handleDownloadRelatorioVeiculo(veiculoDetalhes.id)}
                >
                  Relatório Completo
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => handleOpenDialog(veiculoDetalhes)}
                disabled={!podeEditarVeiculos}
              >
                Editar Veículo
              </Button>
              {podeExcluirVeiculos && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => handleDelete(veiculoDetalhes.id)}
                >
                  Excluir
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderVeiculoCard = (veiculo) => {
    const lucro = parseFloat(veiculo.preco_venda) - parseFloat(veiculo.preco_compra);
    const lucroPerc =
      parseFloat(veiculo.preco_compra) > 0
        ? ((lucro / parseFloat(veiculo.preco_compra)) * 100).toFixed(2)
        : 0;

    const fotoPrincipal = veiculo.fotos?.find(f => f.is_principal)?.url || veiculo.foto_url;
    const isExpanded = expandedVeiculo === veiculo.id;

    return (
      <Grid item xs={12} sm={6} md={4} key={veiculo.id}>
        <Card
          sx={{
            border: isExpanded ? '2px solid' : '1px solid rgba(255,255,255,0.08)',
            borderColor: isExpanded ? 'primary.main' : 'rgba(255,255,255,0.08)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              transform: 'translateY(-2px)',
            },
          }}
          onClick={() => handleExpandVeiculo(veiculo)}
        >
          <Box
            sx={{
              height: 180,
              backgroundColor: 'rgba(255,255,255,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {fotoPrincipal ? (
              <Box component="img" src={fotoPrincipal} alt={veiculo.modelo} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <ImageIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
            )}
            {veiculo.fotos && veiculo.fotos.length > 1 && (
              <Chip
                label={`${veiculo.fotos.length} fotos`}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                }}
              />
            )}
            <Chip
              label={getStatusLabel(veiculo.status)}
              color={getStatusColor(veiculo.status)}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
              }}
            />
          </Box>
          <CardContent>
            <Typography variant="h6">{`${veiculo.marca} ${veiculo.modelo}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {veiculo.placa} • {veiculo.ano_modelo} • {veiculo.cor || '-'}
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {formatCurrency(veiculo.preco_venda)}
              </Typography>
              {podeVerLucros && (
                <Chip
                  label={`${lucroPerc}%`}
                  size="small"
                  color={lucroPerc >= 20 ? 'success' : lucroPerc >= 10 ? 'warning' : 'default'}
                />
              )}
            </Box>
          </CardContent>
          <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
            <Button
              size="small"
              startIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              onClick={(e) => {
                e.stopPropagation();
                handleExpandVeiculo(veiculo);
              }}
            >
              {isExpanded ? 'Fechar' : 'Ver Detalhes'}
            </Button>
            <Box>
              {podeEditarVeiculos && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDialog(veiculo);
                  }}
                  color="primary"
                  size="small"
                >
                  <Edit />
                </IconButton>
              )}
              {podeExcluirVeiculos && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(veiculo.id);
                  }}
                  color="error"
                  size="small"
                >
                  <Delete />
                </IconButton>
              )}
            </Box>
          </CardActions>
        </Card>
      </Grid>
    );
  };

  const colunasTabela = 6
    + (podeVerPrecoCompra ? 1 : 0)
    + (podeVerLucros ? 1 : 0)
    + ((podeEditarVeiculos || podeExcluirVeiculos) ? 1 : 0);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4">Veículos</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(event, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="table">
              <TableRows fontSize="small" />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModule fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
          {podeCadastrarVeiculos && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Novo Veículo
            </Button>
          )}
        </Box>
      </Box>

      {/* Detalhes Expandidos */}
      {expandedVeiculo && renderDetalhesExpandidos()}

      {viewMode === 'table' ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Marca/Modelo</TableCell>
                <TableCell>Placa</TableCell>
                <TableCell>Ano</TableCell>
                <TableCell>Cor</TableCell>
                {podeVerPrecoCompra && <TableCell>Preço Compra</TableCell>}
                <TableCell>Preço Venda</TableCell>
                {podeVerLucros && <TableCell>Lucro %</TableCell>}
                <TableCell>Status</TableCell>
                {(podeEditarVeiculos || podeExcluirVeiculos) && <TableCell align="right">Ações</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {veiculos.map((veiculo) => {
                const lucro = parseFloat(veiculo.preco_venda) - parseFloat(veiculo.preco_compra);
                const lucroPerc =
      parseFloat(veiculo.preco_compra) > 0
        ? ((lucro / parseFloat(veiculo.preco_compra)) * 100).toFixed(2)
        : 0;

                return (
                  <TableRow
                    key={veiculo.id}
                    hover
                    onClick={() => handleExpandVeiculo(veiculo)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: expandedVeiculo === veiculo.id ? 'rgba(214,40,40,0.1)' : 'inherit',
                    }}
                  >
                    <TableCell>{`${veiculo.marca} ${veiculo.modelo}`}</TableCell>
                    <TableCell>{veiculo.placa}</TableCell>
                    <TableCell>{veiculo.ano_modelo}</TableCell>
                    <TableCell>{veiculo.cor || '-'}</TableCell>
                    {podeVerPrecoCompra && <TableCell>{formatCurrency(veiculo.preco_compra)}</TableCell>}
                    <TableCell>{formatCurrency(veiculo.preco_venda)}</TableCell>
                    {podeVerLucros && (
                      <TableCell>
                        <Chip
                          label={`${lucroPerc}%`}
                          color={lucroPerc >= 20 ? 'success' : lucroPerc >= 10 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip label={getStatusLabel(veiculo.status)} color={getStatusColor(veiculo.status)} size="small" />
                    </TableCell>
                    {(podeEditarVeiculos || podeExcluirVeiculos) && (
                      <TableCell align="right">
                        {podeEditarVeiculos && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDialog(veiculo);
                            }}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {podeExcluirVeiculos && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(veiculo.id);
                            }}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {veiculos.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colunasTabela} align="center">
                    Nenhum Veículo encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={2}>
          {veiculos.map(renderVeiculoCard)}
          {veiculos.length === 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, textAlign: 'center' }}>Nenhum Veículo encontrado</Paper>
            </Grid>
          )}
        </Grid>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{editId ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
        <DialogContent>
          <Tabs
            value={abaAtiva}
            onChange={(e, newValue) => setAbaAtiva(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label="Dados do Veículo" />
            <Tab label={`Galeria de Fotos ${fotosGaleria.length > 0 ? `(${fotosGaleria.length})` : ''}`} disabled={!editId} />
          </Tabs>

          {abaAtiva === 0 && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Dados do Veículo
                </Typography>
              </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Marca"
                value={formData.marca}
                onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Modelo"
                value={formData.modelo}
                onChange={(e) => setFormData({ ...formData, modelo: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <InputMask
                mask="aaa-9*99"
                value={formData.placa}
                onChange={(e) => setFormData({ ...formData, placa: e.target.value.toUpperCase() })}
                formatChars={{
                  a: '[A-Za-z]',
                  9: '[0-9]',
                  '*': '[0-9A-Za-z]',
                }}
              >
                {() => <TextField fullWidth required label="Placa" />}
              </InputMask>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Cor"
                value={formData.cor}
                onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                required
                label="Ano Fabricação"
                type="number"
                value={formData.ano_fabricacao}
                onChange={(e) => setFormData({ ...formData, ano_fabricacao: e.target.value })}
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                required
                label="Ano Modelo"
                type="number"
                value={formData.ano_modelo}
                onChange={(e) => setFormData({ ...formData, ano_modelo: e.target.value })}
                inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Renavam"
                value={formData.renavam}
                onChange={(e) => setFormData({ ...formData, renavam: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Chassi"
                value={formData.chassi}
                onChange={(e) => setFormData({ ...formData, chassi: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 17 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quilometragem"
                type="number"
                value={formData.quilometragem}
                onChange={(e) => setFormData({ ...formData, quilometragem: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Combustível"
                value={formData.combustivel}
                onChange={(e) => setFormData({ ...formData, combustivel: e.target.value })}
              >
                <MenuItem value="Gasolina">Gasolina</MenuItem>
                <MenuItem value="Etanol">Etanol</MenuItem>
                <MenuItem value="Flex">Flex</MenuItem>
                <MenuItem value="Diesel">Diesel</MenuItem>
                <MenuItem value="Elétrico">Elétrico</MenuItem>
                <MenuItem value="Híbrido">Híbrido</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Câmbio"
                value={formData.cambio}
                onChange={(e) => setFormData({ ...formData, cambio: e.target.value })}
              >
                <MenuItem value="Manual">Manual</MenuItem>
                <MenuItem value="Automático">Automático</MenuItem>
                <MenuItem value="Automatizado">Automatizado</MenuItem>
                <MenuItem value="CVT">CVT</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Origem do Veículo"
                value={formData.origem_estoque}
                onChange={(e) => setFormData({ ...formData, origem_estoque: e.target.value })}
              >
                <MenuItem value="proprio">Próprio</MenuItem>
                <MenuItem value="consignado">Consignado</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Valores
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Preço de Compra (R$)"
                type="number"
                value={formData.preco_compra}
                onChange={(e) => setFormData({ ...formData, preco_compra: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Preço de Venda (R$)"
                type="number"
                value={formData.preco_venda}
                onChange={(e) => setFormData({ ...formData, preco_venda: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Mês Referência FIPE"
                value={formData.fipe_mes_referencia}
                onChange={(e) => setFormData({ ...formData, fipe_mes_referencia: e.target.value })}
                placeholder="Ex: 06/2026"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Valor FIPE (R$)"
                type="number"
                value={formData.fipe_valor}
                onChange={(e) => setFormData({ ...formData, fipe_valor: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            {podeVerLucros && (
              <Grid item xs={12} sm={4}>
                <Paper
                  sx={{
                    p: 2,
                    backgroundColor:
                      lucroCalculado.percentual >= 20
                        ? 'rgba(58,167,109,0.2)'
                        : 'rgba(201,122,43,0.2)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Lucro Calculado
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {lucroCalculado.percentual}%
                  </Typography>
                  <Typography variant="caption">{formatCurrency(lucroCalculado.lucro)}</Typography>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Foto do Veículo
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <input
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                id="upload-foto"
                type="file"
                onChange={handleFotoSelect}
              />
              <label htmlFor="upload-foto">
                <Button variant="outlined" component="span" startIcon={<PhotoCamera />}>
                  Selecionar Foto
                </Button>
              </label>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Paper
                variant="outlined"
                sx={{
                  height: 160,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  backgroundImage: fotoPreview ? `url(${fotoPreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                {!fotoPreview && <ImageIcon color="disabled" fontSize="large" />}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 2 }}>
                Documentos (PDF, PNG, JPG)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <input
                accept=".pdf,.png,.jpg,.jpeg"
                style={{ display: 'none' }}
                id="upload-documentos"
                multiple
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="upload-documentos">
                <Button variant="outlined" component="span" startIcon={<AttachFile />}>
                  Anexar Documentos
                </Button>
              </label>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Os arquivos enviados ficam salvos no servidor e podem ser consultados por clique no veículo.
              </Typography>
            </Grid>

            {documentosSalvos.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Documentos Salvos ({documentosSalvos.length})
                  </Typography>
                  <List dense>
                    {documentosSalvos.map((arquivo) => (
                      <ListItem
                        key={arquivo.id}
                        onClick={() => openArquivo(arquivo)}
                        sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}
                      >
                        {getFileIcon(arquivo)}
                        <ListItemText
                          primary={arquivo.nome_original}
                          secondary={formatFileSize(arquivo.tamanho)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleDeleteArquivoSalvo(arquivo.id)}>
                            <DeleteOutline />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            {documentos.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Arquivos Anexados ({documentos.length})
                  </Typography>
                  <List dense>
                    {documentos.map((file, index) => (
                      <ListItem key={index}>
                        {getFileIcon(file)}
                        <ListItemText
                          primary={file.name}
                          secondary={formatFileSize(file.size)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                            <DeleteOutline />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={3}
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Informações adicionais sobre o Veículo..."
              />
            </Grid>
          </Grid>
          )}

          {abaAtiva === 1 && editId && (
            <Box sx={{ mt: 2 }}>
              <GaleriaFotos
                veiculoId={editId}
                fotos={fotosGaleria}
                onUpdate={() => loadFotosGaleria(editId)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          {abaAtiva === 0 && (
            <Button onClick={handleSubmit} variant="contained">
              {editId ? 'Atualizar' : 'Salvar'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}












